import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { setUser as setClientUser } from "@/reducers/clientprofile";

import { toggleClientBranchesModal } from "./clientcommon";
import {
  candidateTypeCounts,
  invoiceTypeCounts,
  timesheetTypeCounts,
} from "../Constants/common";
import { routingConstants } from "../Constants/routingConstants";
import { _localSet, saveState } from "../localStorage";
import { updateTypeCount as updateScheduleCount } from "../reducers/clientscandidates";
import { updateTypeCount as updateInvoiceTypeCount } from "../reducers/invoicesCount";
import { updateTypeCount as updateTimeSheetCount } from "../reducers/timesheets/timesheets";
import { _impersonateUser } from "../services/auth";
import { _allowPublishJob } from "../services/clientprofile";
import { toastMessageText } from "../utilities/CommonText";
import {
  apiEncrypt,
  getDecryptedLocalStorageValue,
} from "../utilities/keyEncryption";
import { handleAPICatch, showErrorToast } from "../utilities/utils";

const initialState = {
  session: getDecryptedLocalStorageValue(apiEncrypt("session", "0")) ?? {
    user_id: null,
    user_type: null,
    user_token: null,
  },
  isPremiumAccount: getDecryptedLocalStorageValue("PT1E9PSIsCiJ2Ijox") ?? false,
  clientUserAllPermissions: null,
  user: null,
  isAuthenticated: false,
};

export const allowPublishJob = createAsyncThunk(
  "session/allowPublishJob",
  async (data, { dispatch }) => {
    const response = _allowPublishJob(data);
    response
      .then((res) => {
        const {
          data: {
            client_allow_publish_job,
            subscription_id,
            is_bank_client,
            client_allow_appchat_broadcast = 0,
            client_allow_e_compliance = 0,
          },
        } = res;
        dispatch(
          setSession({
            client_allow_publish_job,
            subscription_id,
            is_bank_client: subscription_id == 1 ? 0 : is_bank_client,
            client_allow_appchat_broadcast,
            client_allow_e_compliance,
          }),
        );
        dispatch(setIsPremiumAccount(subscription_id == 2 ? true : false));
      })
      .catch((error) => {
        handleAPICatch(error);
      });
  },
);

export const handleSubBranchLogin = createAsyncThunk(
  "session/handleSubBranchLogin",
  async (data, { dispatch }) => {
    const {
      branches = {},
      navigate,
      isRedirect = 1,
      activeBranch = {},
      disabledBranch = "",
      loadingBranchId = null,
      setLoadingBranchId = () => {},
    } = data;

    const { client_branch_id, client_name } = branches;

    if (disabledBranch || activeBranch.client_branch_id === client_branch_id) {
      showErrorToast(toastMessageText?.alreadyLoginInBranch(client_name), {
        id: "branch_error",
      });
      return;
    }

    if (loadingBranchId === client_branch_id) return;
    setLoadingBranchId(client_branch_id);

    try {
      const response = await _impersonateUser({ client_id: client_branch_id });
      if (response?.data) {
        await dispatch(
          handleSessionUpdate({ data: response.data, navigate, isRedirect }),
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => setLoadingBranchId(false), 15);
    }
  },
);

export const handleSessionUpdate = createAsyncThunk(
  "session/handleSessionUpdate",
  async ({ data, navigate, isRedirect }, { dispatch, getState }) => {
    const { session, clientProfile } = getState();

    if (session?.session?.user_type !== "Client_User") return;

    const { session: sessionData } = session;
    const { user: userData } = clientProfile;

    const commonData = {
      client_id: data?.client_id,
      original_user: sessionData.user_id,
      client_branch_name: data?.client_branch_name || "",
      client_name: data?.client_branch_name || "",
      client_practice_code: data?.client_practice_code || "",
      qr_code_key: data?.qr_code || "",
      qr_code_updated_at: data?.qr_code_updated_at || "",
      login_as: data?.login_as || "",
      is_bank_client: data?.is_bank_client || "",
    };

    const newStoreData = {
      session: {
        ...session,
        session: {
          ...sessionData,
          ...commonData,
        },
      },
      clientProfile: {
        ...clientProfile,
        user: {
          ...userData,
          ...commonData,
        },
      },
    };

    const resetCounts = (countsArray, updateAction) => {
      const resetArray = countsArray.map((item) => ({
        ...item,
        value: 0,
      }));
      dispatch(updateAction(resetArray));
    };

    resetCounts(candidateTypeCounts, updateScheduleCount);
    resetCounts(timesheetTypeCounts, updateTimeSheetCount);
    resetCounts(invoiceTypeCounts, updateInvoiceTypeCount);

    const callback = () => {
      saveState(newStoreData);
      dispatch(toggleClientBranchesModal(false));
      if (isRedirect) {
        navigate(routingConstants.dashboardRedirect, {
          state: { _for: routingConstants.dashboard },
        });
      }
    };

    dispatch(setSession(commonData));
    dispatch(setClientUser({ ...commonData, callback }));
    dispatch(setIsPremiumAccount(data?.subscription_id === 2 ? true : false));
  },
);

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },

    setSession: (state, action) => {
      let callback = action.payload.callback ? action.payload.callback : null;
      if (action.payload.callback) {
        delete action.payload.callback;
      }
      // Update state
      state.session = {
        ...state.session,
        ...action.payload,
      };

      // Sync permissions: when session contains allPermissions, populate clientUserAllPermissions
      if (action.payload.allPermissions !== undefined) {
        const permissions = action.payload.allPermissions;
        if (permissions && permissions.is_admin == 1) {
          const fullAccess = {};
          Object.keys(permissions).forEach((key) => {
            fullAccess[key] = 1;
          });
          state.clientUserAllPermissions = fullAccess;
        } else {
          state.clientUserAllPermissions = permissions || null;
        }
      }

      try {
        const encryptedValue = apiEncrypt(JSON.stringify(action.payload));
        _localSet(apiEncrypt("session", "0"), encryptedValue);
      } catch (error) {
        console.error("Failed to encrypt or store session---:", error);
      }
      setTimeout(() => {
        callback && callback();
      }, 50);
    },
    resetSession: (state) => {
      state.session = {
        user_id: null,
        user_type: null,
        user_token: null,
      };
      state.user = null;
      state.isAuthenticated = false;
    },
    setIsAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
    },
    setAllPermissions: (state, action) => {
      const permissions = action.payload;
      if (!permissions) {
        state.clientUserAllPermissions = null;
        return;
      }
      // If is_admin is set, grant full access to every permission key
      if (permissions.is_admin == 1) {
        const fullAccess = {};
        Object.keys(permissions).forEach((key) => {
          fullAccess[key] = 1;
        });
        state.clientUserAllPermissions = fullAccess;
      } else {
        state.clientUserAllPermissions = permissions;
      }
    },
    setIsPremiumAccount: (state, action) => {
      state.isPremiumAccount = action.payload;
      try {
        const encryptedValue = apiEncrypt(JSON.stringify(action.payload));
        _localSet("PT1E9PSIsCiJ2Ijox", encryptedValue);
      } catch (error) {
        console.error("Failed to encrypt or store isPremiumAccount---:", error);
      }
    },
  },
});

export const {
  setUser,
  setSession,
  logout,
  resetSession,
  setIsAuthenticated,
  setAllPermissions,
  setIsPremiumAccount,
} = sessionSlice.actions;

export default sessionSlice.reducer;
